import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Mail, Lock, Loader2 } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex items-center justify-center px-4 py-12">
      <main className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Anmelden</h1>
        </div>

        <div className="rounded-2xl border border-[#2a2a30] bg-[#17171a] p-6 sm:p-8 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.6)]">
          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider">
                E-Mail
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6a73]" />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-11 pl-10 pr-3 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-white placeholder:text-[#6a6a73] transition-colors"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider">
                Passwort
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6a73]" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 pl-10 pr-3 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-white placeholder:text-[#6a6a73] transition-colors"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div
                className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-lg bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold shadow-[0_8px_24px_rgba(255,75,80,0.3)] hover:shadow-[0_10px_30px_rgba(255,75,80,0.45)] transition-shadow disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="login-submit-button"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Einloggen..." : "Einloggen"}
            </button>
          </form>
        </div>

        <p
          className="mt-6 text-center text-xs text-[#6a6a73]"
          data-testid="login-private-notice"
        >
          Private Anwendung. Kein öffentlicher Dienst.
        </p>
      </main>
    </div>
  );
}
